import { useCallback, useEffect, useMemo, useState } from 'react';
import { RuleModel } from '../../../../types';
// import { getSchemaKeys } from '../../../shared/simple-form-page';
import { pagesConfig } from '../../../shared/pages-config';
import {
  FormSubSteps,
  StepConfig,
} from '../../../shared/shared-toolbar-form-stepper';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getAllPoliciesQuery,
  getDomainsQuery,
  getSharedDrivesByIdsQuery,
  getDocById,
  UserKeys,
  getAllResourceCalendars,
} from '../../../api/users-api';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  addRule,
  RulesKeys,
  editRule,
  saveRule,
  listAllRules,
} from '../../../api/rules-engine-api';
import {
  cloneRule,
  getRulesEngineFormDefaultValues,
  prepareRuleData,
} from './rules-engine-form-utils';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  RulesEngineRoles,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';
import RulesEngineFormSkeleton from './steps/rules-engine-form-skeleton';
import RulesEngineBasicInformationStep, {
  RulesEngineBasicInformationStepSchema,
} from './steps/rules-engine-basic-information-step';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { actionOptions } from './action-options';
import RulesEngineDrawerDetails from './rules-engine-drawer-details';
import DeleteRuleDialog from './delete-rule-dialog';
import DisableRuleDialog from './disable-rule-dialog';
import EnableRuleDialog from './enable-rule-dialog';
import EditRuleDialog from './edit-rule-dialog';

import RuleSummaryPanel from './rule-summary-panel';
import NotFoundPage from '../../../shared/not-found-page';
import { Messages } from '../../../utils/message-constants';
import SharedToolbarFormPage from '../../../shared/shared-toolbar-form-page';
import SharedPageHeader from '../../../shared/shared-page-header';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PlayArrow from '@mui/icons-material/PlayArrow';
import RulesEngineWhenStep, {
  RulesEngineWhenStepSchema,
} from './steps/rules-engine-when-step';
import ConditionBlocks from './condition-blocks';
import ThenAction from './steps/then-action';
import RulesEngineHelp from './rules-engine-help';

const EMPTY_ARRAY: [] = [];
import { triggerEventOptions } from './trigger-event-options';

import Stack from '@mui/material/Stack';
import Bolt from '@mui/icons-material/Bolt';
import TextSnippet from '@mui/icons-material/TextSnippet';

export type RulesEngineSourceType =
  | 'User'
  | 'Group'
  | 'Domain'
  | 'Orgs'
  | 'Resource Calendar';

export interface TriggerSource {
  type: string;
  value: string;
  driveFolderOwner?: string;
  driveFolderName?: string;
  driveFolderId?: string;
  selectedSharedDriveId?: string;
}

interface RulesEngineFormPageProps {
  pageMode: 'create' | 'edit' | 'clone';
  title: string;
}

export interface ProfileFieldFilter {
  profileInfoType?: string;
  profileFieldType?: string;
  profileFieldValues?: { value?: string }[];
  profileFieldValue?: string;
  profileFieldRegex?: string;
  profileFieldValueExact?: boolean;
  valueInputMode?: 'fieldValue' | 'regex';
  fieldDataType?: string;
}

export interface Condition {
  id: string;
  fieldInfoType: string;
  fieldName: string;
  operator:
    | 'contains'
    | 'equals'
    | 'notContains'
    | 'notEquals'
    | 'startsWith'
    | 'endsWith'
    | 'larger'
    | 'smaller'
    | 'between'
    | 'greaterThan'
    | 'greaterThanOrEqual'
    | 'lessThan'
    | 'lessThanOrEqual'
    | 'isEmpty'
    | 'isNotBetween';
  value: string;
  secondaryValue?: string | null;
  unit?: string | null;
  logicalOperator?: 'AND' | 'OR' | '' | null;
}

export interface ConditionBlock {
  id: string;
  conditions: Condition[];
}

export interface RulesEngineForm
  extends Omit<RuleModel, 'profileFieldFilter' | 'conditionBlocks'> {
  triggerSources?: TriggerSource[];
  hasFileTypeFilter?: boolean;
  hasFileSizeFilter?: boolean;
  hasSharingPermissionsFilter?: boolean;
  hasSelectedDomainSharing?: boolean;
  selectedSharingOption?:
    | 'fileIsSharedToDomain'
    | 'fileIsSharedPublic'
    | 'fileSharedToSpecificUser'
    | 'fileSharedToUserExternalToDomain'
    | 'fileSharedToUserInternalToDomain';
  sizeComparison?: 'larger' | 'smaller' | 'between';
  sizeType?: 'MB' | 'KB' | 'Byte';
  hasProfileFieldFilter?: boolean;
  groupInfo?: {
    groupEmail: string[];
    groupRole: string[];
  };
  removeGroupInfo?: {
    groupEmail: string[];
  };
  profileFieldFilter?: ProfileFieldFilter;
  allSource?: boolean;
  targetsExcluded?: string[];
  conditionBlocks?: ConditionBlock[];
}

const CalendarResourcesStepSchema = z.object({
  allSource: z.boolean(),
  triggerInfo: z.object({
    triggerCalendarSources: z.array(z.string()),
  }),
});

const DriveFiltersSchema = z
  .object({
    hasFileTypeFilter: z.boolean().optional(),
    hasFileSizeFilter: z.boolean().optional(),
    sizeComparison: z.enum(['larger', 'smaller', 'between']).optional(),
    sizeType: z.enum(['MB', 'KB', 'Byte']).optional(),
    hasSharingPermissionsFilter: z.boolean().optional(),
    selectedSharingOption: z
      .enum([
        'fileIsSharedToDomain',
        'fileIsSharedPublic',
        'fileSharedToSpecificUser',
        'fileSharedToUserExternalToDomain',
        'fileSharedToUserInternalToDomain',
      ])
      .optional(),
    hasSelectedDomainSharing: z.boolean().optional(),
    triggerInfo: z.object({
      fileMimeTypes: z.array(z.string()),
      driveFileSizeFloor: z.number(),
      driveFileSizeCeiling: z.number(),
      fileIsSharedToDomain: z.boolean(),
      fileIsSharedPublic: z.boolean(),
      fileSharedToUserExternalToDomain: z.boolean().catch(false),
      fileSharedToUserInternalToDomain: z.boolean().catch(false),
      fileSharedToSpecificUser: z.array(z.string()),
      canFindInSearchResults: z.boolean().catch(false),
      mustHaveLinkToAccess: z.boolean().catch(false),
    }),
  })
  .refine(
    (data) => {
      if (data.hasFileTypeFilter) {
        return (
          data.triggerInfo.fileMimeTypes &&
          data.triggerInfo.fileMimeTypes.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select a file type.',
      path: ['triggerInfo', 'fileMimeTypes'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const floor = data.triggerInfo.driveFileSizeFloor;
        const comparison = data.sizeComparison;
        switch (comparison) {
          case 'larger':
          case 'between':
            return floor > 0;
          default:
            return true;
        }
      }
      return true;
    },
    {
      message: 'Must be greater than 0.',
      path: ['triggerInfo', 'driveFileSizeFloor'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const ceiling = data.triggerInfo.driveFileSizeCeiling;
        const comparison = data.sizeComparison;
        switch (comparison) {
          case 'smaller':
          case 'between':
            return ceiling > 0;
          default:
            return true;
        }
      }
      return true;
    },
    {
      message: 'Must be greater than 0.',
      path: ['triggerInfo', 'driveFileSizeCeiling'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const ceiling = data.triggerInfo.driveFileSizeCeiling ?? 0;
        const floor = data.triggerInfo.driveFileSizeFloor ?? 0;
        const comparison = data.sizeComparison;
        if (comparison === 'between') {
          return floor > 0 && ceiling > floor;
        }
      }
      return true;
    },
    {
      message: 'Must be larger than floor.',
      path: ['triggerInfo', 'driveFileSizeCeiling'],
    }
  )
  .refine(
    (data) => {
      if (
        data.hasSharingPermissionsFilter &&
        data.selectedSharingOption === 'fileSharedToSpecificUser'
      ) {
        return (
          data.triggerInfo.fileSharedToSpecificUser &&
          data.triggerInfo.fileSharedToSpecificUser.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select a user.',
      path: ['triggerInfo', 'fileSharedToSpecificUser'],
    }
  );

const ActionsSchema = z
  .object({
    actionInfo: z.object({
      actionTodo: z
        .array(
          z.enum(
            actionOptions.map((action) => action.value) as [string, ...string[]]
          )
        )
        .min(1, 'Please add at least one action.'),
      payload: z.string(),
      endpointUrl: z.string(),
      requestMethod: z.string(),
      newPassword: z.string(),
      timeZone: z.string(),
      occurrenceLimit: z.number(),
    }),
    emailInfo: z.object({
      emailNotificationEmails: z.array(z.string()),
      ccEmails: z.array(z.string()),
      bccEmails: z.array(z.string()),
      emailSubject: z.string(),
      emailMessage: z.string(),
    }),
    chatInfo: z.object({
      chatUrl: z.string(),
      chatMessage: z.string(),
    }),
    policyInfo: z.object({
      policyId: z.number(),
      policyType: z.string(),
    }),
    groupInfo: z.object({
      groupEmail: z.array(z.string()),
      groupRole: z.array(z.string()),
    }),
    removeGroupInfo: z.object({
      groupEmail: z.array(z.string()),
    }),
    groupSettingsInfo: z.object({
      groupTemplateId: z.union([z.number(), z.string()]),
    }),
  })
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('limitOccurrences')) {
        return (
          data.actionInfo.occurrenceLimit && data.actionInfo.occurrenceLimit > 0
        );
      }
      return true;
    },
    {
      message: 'Occurrence limit must be greater than 0 for calendar events.',
      path: ['actionInfo', 'occurrenceLimit'],
    }
  )
  .refine(
    (data) => {
      if (
        data.actionInfo.actionTodo.includes('sendEmail') ||
        data.actionInfo.actionTodo.includes('sendEmailTemplate')
      ) {
        return (
          data.emailInfo.emailNotificationEmails &&
          data.emailInfo.emailNotificationEmails.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select at least one email recipient.',
      path: ['emailInfo', 'emailNotificationEmails'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('sendChatMessage')) {
        return data.chatInfo.chatUrl;
      }
      return true;
    },
    { message: 'Chat URL are required.', path: ['chatInfo', 'chatUrl'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('sendChatMessage')) {
        return data.chatInfo.chatMessage;
      }
      return true;
    },
    { message: 'Chat message are required.', path: ['chatInfo', 'chatMessage'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('runPolicy')) {
        return data.policyInfo.policyId;
      }
      return true;
    },
    { message: 'Please select a policy.', path: ['policyInfo', 'policyId'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('addToGroup')) {
        return data.groupInfo.groupEmail.length > 0;
      }
      return true;
    },
    {
      message: 'Please select at least one group.',
      path: ['groupInfo', 'groupEmail'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('removeFromGroup')) {
        return data.removeGroupInfo.groupEmail.length > 0;
      }
      return true;
    },
    {
      message: 'Please select at least one group.',
      path: ['removeGroupInfo', 'groupEmail'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('applyGroupTemplate')) {
        return Number(data.groupSettingsInfo.groupTemplateId) > 0;
      }
      return true;
    },
    {
      message: 'Please select a group template.',
      path: ['groupSettingsInfo', 'groupTemplateId'],
    }
  );

function validateFieldValue(val: string, fieldDataType: string): string | null {
  switch (fieldDataType) {
    case 'WHOLE_NUMBER':
      return /^-?\d+$/.test(val) ? null : 'Must be a whole number.';
    case 'DECIMAL':
      return /^-?\d+(\.\d+)?$/.test(val) ? null : 'Must be a decimal number.';
    case 'DATE':
      return /^\d{4}-\d{2}-\d{2}$/.test(val)
        ? null
        : 'Must be a valid date (YYYY-MM-DD).';
    case 'EMAIL':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
        ? null
        : 'Must be a valid email address.';
    case 'PHONE':
      return /^\+?[0-9\s\-().]{7,20}$/.test(val)
        ? null
        : 'Must be a valid phone number.';
    case 'BOOL':
      return val === 'true' || val === 'false' ? null : 'Must be Yes or No.';
    default:
      return null;
  }
}

const AdminFiltersSchema = z
  .object({
    hasProfileFieldFilter: z.boolean().optional(),
    profileFieldFilter: z.object({
      profileInfoType: z.string().optional(),
      profileFieldType: z.string().optional(),
      profileFieldValues: z
        .array(z.object({ value: z.string().optional() }))
        .optional(),
      profileFieldValue: z.string().optional(),
      profileFieldValueExact: z.boolean().optional(),
      profileFieldRegex: z.string().optional(),
      valueInputMode: z.enum(['fieldValue', 'regex']).optional(),
      fieldDataType: z.string().optional(),
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.hasProfileFieldFilter) return;

    const {
      profileFieldType,
      profileFieldValues,
      profileFieldRegex,
      valueInputMode,
      fieldDataType,
    } = data.profileFieldFilter;
    const mode = valueInputMode ?? 'fieldValue';

    if (!profileFieldType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Profile field type is required.',
        path: ['profileFieldFilter', 'profileFieldType'],
      });
    }

    if (data.hasProfileFieldFilter && mode === 'fieldValue') {
      const hasValues =
        (profileFieldValues ?? []).some(
          (v) => v.value && v.value.trim() !== ''
        ) || (data.profileFieldFilter.profileFieldValue ?? '').trim() !== '';

      if (!hasValues) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one field value is required.',
          path: ['profileFieldFilter', 'profileFieldValues', 0, 'value'],
        });
      }

      profileFieldValues?.forEach((item, i) => {
        if (!item.value || item.value.trim() === '') return;
        const issue = validateFieldValue(item.value, fieldDataType ?? 'TEXT');
        if (issue) {
          ctx.addIssue({
            code: 'custom',
            message: issue,
            path: ['profileFieldFilter', 'profileFieldValues', i, 'value'],
          });
        }
      });
    }

    if (data.hasProfileFieldFilter && mode === 'regex') {
      if (!profileFieldRegex?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'A regular expression is required.',
          path: ['profileFieldFilter', 'profileFieldRegex'],
        });
      } else {
        try {
          new RegExp(profileFieldRegex);
        } catch {
          ctx.addIssue({
            code: 'custom',
            message: 'Invalid regular expression.',
            path: ['profileFieldFilter', 'profileFieldRegex'],
          });
        }
      }
    }
  });

const GroupFiltersSchema = z
  .object({
    groupFilter: z
      .object({
        hasGroupNameFilter: z.boolean().optional(),
        groupNameMode: z.enum(['name', 'regex']).optional(),
        groupName: z.string().optional(),
        groupNameExact: z.boolean().optional(),
        groupNameRegex: z.string().optional(),
        hasMemberCountFilter: z.boolean().optional(),
        memberCountMode: z.enum(['single', 'range']).optional(),
        memberCountOperator: z
          .enum([
            'equals',
            'greaterThan',
            'lessThan',
            'greaterThanOrEqual',
            'lessThanOrEqual',
          ])
          .optional(),
        memberCount: z.number().optional(),
        memberCountRangeLessThan: z.number().optional(),
        memberCountRangeGreaterThan: z.number().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const filter = data.groupFilter;
    if (!filter) return;

    if (filter.hasGroupNameFilter) {
      const mode = filter.groupNameMode ?? 'name';
      if (mode === 'name' && !(filter.groupName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'A group name is required.',
          path: ['groupFilter', 'groupName'],
        });
      }
      if (mode === 'regex') {
        if (!(filter.groupNameRegex ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'A regular expression is required.',
            path: ['groupFilter', 'groupNameRegex'],
          });
        } else {
          try {
            new RegExp(filter.groupNameRegex as string);
          } catch {
            ctx.addIssue({
              code: 'custom',
              message: 'Invalid regular expression.',
              path: ['groupFilter', 'groupNameRegex'],
            });
          }
        }
      }
    }

    if (filter.hasMemberCountFilter) {
      const mode = filter.memberCountMode ?? 'single';
      const isInvalidCount = (val: number | undefined | null) =>
        val === undefined || val === null || Number.isNaN(val) || val < 0;

      if (mode === 'single') {
        if (isInvalidCount(filter.memberCount)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid member count is required.',
            path: ['groupFilter', 'memberCount'],
          });
        }
      } else {
        if (isInvalidCount(filter.memberCountRangeLessThan)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid value is required.',
            path: ['groupFilter', 'memberCountRangeLessThan'],
          });
        }
        if (isInvalidCount(filter.memberCountRangeGreaterThan)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid value is required.',
            path: ['groupFilter', 'memberCountRangeGreaterThan'],
          });
        }
        if (
          !isInvalidCount(filter.memberCountRangeLessThan) &&
          !isInvalidCount(filter.memberCountRangeGreaterThan) &&
          (filter.memberCountRangeGreaterThan as number) <
            (filter.memberCountRangeLessThan as number)
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              "The 'greater than' value must be greater than or equal to the 'less than' value.",
            path: ['groupFilter', 'memberCountRangeGreaterThan'],
          });
        }
      }
    }
  });

export const ConditionBlocksSchema = z
  .object({
    conditionBlocks: z
      .array(
        z.object({
          id: z.string(),
          conditions: z
            .array(
              z
                .object({
                  id: z.string(),
                  fieldInfoType: z.string().min(1, 'Field type is required.'),
                  fieldName: z.string().min(1, 'Field name is required.'),
                  operator: z.enum([
                    'contains',
                    'equals',
                    'notContains',
                    'notEquals',
                    'startsWith',
                    'endsWith',
                    'larger',
                    'smaller',
                    'between',
                    'greaterThan',
                    'greaterThanOrEqual',
                    'lessThan',
                    'lessThanOrEqual',
                    'isEmpty',
                    'isNotBetween',
                  ]),
                  value: z.string(),
                  secondaryValue: z.string().nullable().optional(),
                  unit: z.string().nullable().optional(),
                  logicalOperator: z
                    .union([z.enum(['AND', 'OR']), z.literal('')])
                    .optional()
                    .nullable(),
                })
                .refine(
                  (data) => {
                    const fieldsWithoutValue = [
                      'sharedToUserInternalToDomain',
                      'sharedToUserExternalToDomain',
                      'fileSharedToUserExternalToDomain',
                      'fileSharedToUserInternalToDomain',
                      'fileIsSharedPublic',
                    ];
                    return (
                      data.operator === 'isEmpty' ||
                      fieldsWithoutValue.includes(data.fieldName) ||
                      data.value.length > 0
                    );
                  },
                  { message: 'Value is required.', path: ['value'] }
                )
                .refine(
                  (data) => {
                    if (
                      data.operator === 'between' ||
                      data.operator === 'isNotBetween'
                    ) {
                      return (
                        data.secondaryValue && data.secondaryValue.length > 0
                      );
                    }
                    return true;
                  },
                  {
                    message: 'Second value is required.',
                    path: ['secondaryValue'],
                  }
                )
                .refine(
                  (data) => {
                    if (data.fieldName === 'gPanelRole') {
                      return data.value.length > 0;
                    }
                    return true;
                  },
                  { message: 'Please select a role.', path: ['value'] }
                )
            )
            .min(1, 'Each condition block must have at least one condition.'),
        })
      )
      .optional(),
  })
  .optional();

const PersonalCalendarFiltersSchema = z
  .object({
    personalCalendarFilter: z
      .object({
        hasPersonalCalendarFilter: z.boolean().optional(),
        sharedToUserExternalToDomain: z.boolean().optional(),
        sharedToUserInternalToDomain: z.boolean().optional(),
        sharedToSpecificUsers: z.array(z.string()).optional(),
        sharedToGroups: z.array(z.string()).optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const filter = data.personalCalendarFilter;
    if (!filter || !filter.hasPersonalCalendarFilter) return;

    const isAnyFilterSelected =
      filter.sharedToUserExternalToDomain ||
      filter.sharedToUserInternalToDomain ||
      (filter.sharedToSpecificUsers &&
        filter.sharedToSpecificUsers.length > 0) ||
      (filter.sharedToGroups && filter.sharedToGroups.length > 0);

    if (!isAnyFilterSelected) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one calendar sharing filter must be selected.',
        path: ['personalCalendarFilter', 'hasPersonalCalendarFilter'],
      });
    }
  });

export const RulesEngineSchema = RulesEngineBasicInformationStepSchema.and(
  RulesEngineWhenStepSchema
)
  .and(DriveFiltersSchema)
  .and(CalendarResourcesStepSchema)
  .and(ActionsSchema)
  .and(
    z.object({
      id: z.number().catch(0),
      basicInfo: z.object({
        status: z.enum(['Enabled', 'Disabled', 'Draft']).catch('Draft'),
        creator: z.string().catch(''),
        createdDate: z.number().catch(0),
        lastModifiedDate: z.number().catch(0),
      }),
    })
  )
  .and(AdminFiltersSchema)
  .and(GroupFiltersSchema)
  .and(PersonalCalendarFiltersSchema)
  .and(ConditionBlocksSchema)
  .superRefine((data, ctx) => {
    if (
      data.triggerInfo.triggerGroup === 'calendar' &&
      data.triggerInfo.triggerEvent !== 'change_calendar_acls' &&
      !data.allSource &&
      data.triggerInfo.triggerCalendarSources &&
      data.triggerInfo.triggerCalendarSources.length <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one trigger source is required for calendar events.',
        path: ['triggerInfo', 'triggerCalendarSources'],
      });
    }
  });

export type RulesEngineFormValues = z.infer<typeof RulesEngineSchema>;
export default function RulesEngineFormPage({
  pageMode,
  title,
}: RulesEngineFormPageProps) {
  const { changeLogRoles, loggedInUser, toolsRoles, features } =
    useUserSettings();
  const [activeStep, setActiveStep] = useState(0);
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [dialogSubmitAction, setDialogSubmitAction] = useState<
    'publish' | 'draft' | null
  >(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [actionSubSteps, setActionSubSteps] = useState<FormSubSteps[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const [searchParams, setSearchParams] = useSearchParams();
  const { id: selectedRuleIdFromPath } = useParams();
  const location = useLocation();
  const fromTemplates = location.state?.from === 'templates';
  const templateFromState = location.state?.template as RuleModel | undefined;
  const selectedRuleIdFromUrl = searchParams.get('selectedRule')
    ? searchParams.get('selectedRule')
    : pageMode === 'edit' && selectedRuleIdFromPath;

  const { data: allPolicies, isLoading: isPolicyLoading } = useQuery(
    getAllPoliciesQuery()
  );

  const [openDeleteRuleDialog, setOpenDeleteRuleDialog] = useState(false);
  const [openDisableRuleDialog, setOpenDisableRuleDialog] = useState(false);
  const [openEnableRuleDialog, setOpenEnableRuleDialog] = useState(false);
  const [openEditRuleDialog, setOpenEditRuleDialog] = useState(false);

  const { data: allRulesFromApi, isLoading: isLoadingAllRules } = useQuery({
    queryKey: [RulesKeys.RulesList],
    queryFn: listAllRules,
  });

  const sourceRuleForForm = useMemo(() => {
    if (pageMode === 'create' || !allRulesFromApi) return null;

    const rule = allRulesFromApi.find(
      (r) => r.id.toString() === selectedRuleIdFromPath
    );

    if (!rule) return null;

    return pageMode === 'clone' ? cloneRule(rule) : rule;
  }, [allRulesFromApi, pageMode, selectedRuleIdFromPath]);

  const initialSourceType = useMemo(() => {
    const rule = sourceRuleForForm || templateFromState;
    if (rule?.triggerInfo?.triggerEvent) {
      const eventOption = triggerEventOptions.find(
        (opt) => opt.value === rule.triggerInfo.triggerEvent
      );
      return (
        (eventOption?.lowestEntityAllowed as RulesEngineSourceType) || 'Orgs'
      );
    }
    return 'Orgs';
  }, [sourceRuleForForm, templateFromState, triggerEventOptions]);

  const [userSelectedSourceType, setUserSelectedSourceType] =
    useState<RulesEngineSourceType | null>(null);

  const sourceType = userSelectedSourceType || initialSourceType;

  const selectedRule =
    allRulesFromApi?.find((r) => r.id.toString() === selectedRuleIdFromUrl) ||
    null;

  const ruleId = sourceRuleForForm?.id || 0;
  const lastSavedDate = sourceRuleForForm?.basicInfo.lastModifiedDate || null;

  const sharedDriveIdsToFetch =
    sourceRuleForForm?.triggerInfo?.sharedDriveIds || EMPTY_ARRAY;
  const hasBeenSaved = pageMode === 'edit' && !!sourceRuleForForm;
  const { data: sharedDrives } = useQuery(
    getSharedDrivesByIdsQuery(sharedDriveIdsToFetch)
  );

  const userDriveSourcesToFetch =
    sourceRuleForForm?.triggerInfo?.driveFolderTriggerSources || EMPTY_ARRAY;

  const userDriveFoldersQueries = useMemo(
    () =>
      userDriveSourcesToFetch
        .filter((s) => s.driveFolderId && s.driveFolderOwner)
        .map((source) => ({
          queryKey: [
            UserKeys.DriveFolder,
            source.driveFolderId,
            source.driveFolderOwner,
          ],
          queryFn: () =>
            getDocById(source.driveFolderOwner, source.driveFolderId),
        })),
    [userDriveSourcesToFetch]
  );

  // The combine function now just passes through the raw results array from useQueries
  const combineUserDriveFolders = useCallback(
    (results: any[]) => ({
      rawResults: results,
      pending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    }),
    []
  );

  const {
    rawResults: rawUserDriveFolderResults,
    pending: pendingUserDriveFolders,
    isError: hasUserDriveFolderError,
  } = useQueries({
    queries: userDriveFoldersQueries,
    combine: combineUserDriveFolders,
  });

  // Process the raw results into a stable array of Doc objects using useMemo
  const userDriveFolders = useMemo(() => {
    return rawUserDriveFolderResults
      .filter((result) => result.status === 'success' && result.data)
      .map((result) => result.data);
  }, [rawUserDriveFolderResults]);

  const openRulesDetailsDrawer =
    !!searchParams.get('selectedRule') && !!selectedRule && !isLoadingAllRules;

  const navigate = useNavigate();
  const {
    autoSelectPolicyId,
    // autoSelectEmailTemplateId,
    emailSubject: emailSubjectFromState,
    emailMessage: emailMessageFromState,
  } = (location.state as {
    autoSelectPolicyId?: number;
    emailSubject?: string;
    emailMessage?: string;
    autoSelectEmailTemplateId?: number;
  } | null) || {};

  const { data: domains } = useQuery(getDomainsQuery());

  const formValues = useMemo(() => {
    if (sourceRuleForForm) {
      return getRulesEngineFormDefaultValues({
        pageMode: pageMode,
        selectedRule: sourceRuleForForm, // For edit/clone
        allPolicies,
        sharedDrives,
        userDriveFolders,
        loggedInUser,
        features,
      });
    } else if (pageMode === 'create' && templateFromState) {
      return getRulesEngineFormDefaultValues({
        pageMode: 'clone', // Treat template as a clone to hydrate form
        selectedRule: templateFromState,
        allPolicies,
        sharedDrives,
        userDriveFolders,
        loggedInUser,
        autoSelectPolicyId,
        emailSubject: emailSubjectFromState,
        emailMessage: emailMessageFromState,
        features,
      });
    }
    return getRulesEngineFormDefaultValues({
      pageMode: pageMode,
      allPolicies,
      userDriveFolders,
      sharedDrives,
      loggedInUser,
      autoSelectPolicyId,
      emailSubject: emailSubjectFromState,
      emailMessage: emailMessageFromState,
      features,
    });
  }, [
    sourceRuleForForm,
    pageMode,
    templateFromState,
    loggedInUser,
    allPolicies,
    sharedDrives,
    userDriveFolders,
    autoSelectPolicyId,
    emailSubjectFromState,
    emailMessageFromState,
    features,
  ]);

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    reset,
    trigger,
    clearErrors,
  } = useForm<RulesEngineFormValues>({
    values: formValues,
    resolver: zodResolver(RulesEngineSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      basicInfo: {
        // Keep minimal defaults, `values` will populate the rest
        ruleName: '',
        description: '',
      },
      triggerInfo: {
        occursEverytime: true,
        hasTimeDuration: false,
        triggerGroup: 'admin',
        triggerEvent: 'CREATE_USER',
        triggerThreshold: 1,
        triggerDuration: 1,
        triggerDurationType: 'days',
        triggerDomainSources: [],
        triggerGroupSources: [],
        triggerOrgSources: [],
        triggerUserSources: [],
        triggerCalendarSources: [],
        sharedDriveIds: [],
        driveRegExs: [],
        driveFolderTriggerSources: [],
        driveFileSizeFloor: 0,
        driveFileSizeCeiling: 0,
        //driveFileThreshold: 0,
        fileMimeTypes: [],
        fileIsSharedToDomain: false,
        fileIsSharedPublic: false,
        fileSharedToSpecificUser: [],
        canFindInSearchResults: false,
        mustHaveLinkToAccess: false,
        fileSharedToUserExternalToDomain: false,
        fileSharedToUserInternalToDomain: false,
      },
      actionInfo: {
        actionTodo: [],
        payload: '',
        endpointUrl: '',
        requestMethod: 'POST',
        newPassword: '',
        timeZone: 'UTC',
        occurrenceLimit: 0,
      },
      emailInfo: {
        emailNotificationEmails: [],
        ccEmails: [],
        bccEmails: [],
        emailSubject: '',
        emailMessage: '',
      },
      chatInfo: {
        chatUrl: '',
        chatMessage: '',
      },
      policyInfo: {
        policyId: 0,
        policyType: '',
      },
      allSource: false,
      groupInfo: {
        groupEmail: [],
        groupRole: [],
      },
      removeGroupInfo: {
        groupEmail: [],
      },
      groupSettingsInfo: {
        groupTemplateId: 0,
      },
      triggerSources: [],
      profileFieldFilter: {
        profileInfoType: '',
        profileFieldType: '',
        profileFieldValues: [{ value: '' }],
        profileFieldValue: '',
        profileFieldRegex: '',
        profileFieldValueExact: false,
        valueInputMode: 'fieldValue',
        fieldDataType: '',
      },
      hasProfileFieldFilter: false,
      hasFileTypeFilter: false,
      hasFileSizeFilter: false,
      hasSharingPermissionsFilter: false,
      hasSelectedDomainSharing: false,
      sizeComparison: 'larger',
      sizeType: 'MB',
      selectedSharingOption: undefined,
      conditionBlocks: [],
    },
  });

  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');

  const isPersonalCalendar =
    triggerGroup === 'calendar' && triggerEvent === 'change_calendar_acls';

  const { data: resourceCalendars, isLoading: isLoadingCalendars } = useQuery({
    queryKey: [UserKeys.ResourceCalendars],
    queryFn: () => getAllResourceCalendars(),
    enabled: triggerGroup === 'calendar',
  });

  const { mutate: saveDraft, isPending: isSavingDraft } = useMutation({
    mutationFn: (data: RulesEngineForm & { showSnackbar?: boolean }) => {
      const { showSnackbar, ...dataWithoutFlag } = data;
      const formattedData = prepareRuleData(dataWithoutFlag);
      formattedData.id = ruleId;
      formattedData.basicInfo.status = 'Draft';
      formattedData.basicInfo.lastModifiedDate = Date.now();

      // Set createdDate for new drafts (when ruleId is 0)
      if (ruleId === 0) {
        formattedData.basicInfo.createdDate = Date.now();
      }

      return saveRule(formattedData, changeLogMessage).then((result) => ({
        ...result,
        showSnackbar,
      }));
    },
    onSuccess: (savedRule) => {
      const { showSnackbar, ...ruleData } = savedRule;

      // Update the query cache to reflect the newly saved rule.
      // This makes the cache the source of truth and avoids stale local state.
      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[] | undefined) => {
          const ruleExists = oldData?.some((r) => r.id === ruleData.id);
          if (ruleExists) {
            return oldData?.map((r) => (r.id === ruleData.id ? ruleData : r));
          }
          return [...(oldData || []), ruleData];
        }
      );

      setValue(
        'basicInfo.lastModifiedDate',
        ruleData.basicInfo.lastModifiedDate
      );
      if (showSnackbar) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Rule saved as draft. You can resume editing anytime.',
        });

        bypass();
        navigate(`/${pagesConfig.rulesEngine.link}`);
      }
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue saving the draft. Please try again.',
      });
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: RulesEngineForm) => {
      const formattedData = prepareRuleData(data);

      formattedData.basicInfo.status = 'Enabled';
      if (pageMode === 'edit') {
        return editRule(formattedData, changeLogMessage);
      } else {
        return addRule(formattedData, changeLogMessage);
      }
    },
    onSuccess: (returnedData) => {
      const successMessage =
        pageMode === 'edit'
          ? Messages.Success.Updated('Automation rule')
          : Messages.Success.Created('Automation rule');

      setSnackbar({
        open: true,
        severity: 'success',
        message: successMessage,
      });

      queryClient.invalidateQueries({ queryKey: [RulesKeys.RulesList] });
      setChangeLogMessage('');

      if (pageMode === 'edit') {
        const formCompatibleData = getRulesEngineFormDefaultValues({
          pageMode: 'edit',
          selectedRule: returnedData,
          allPolicies,
          sharedDrives,
          userDriveFolders,
          loggedInUser,
          features,
        });
        reset(formCompatibleData);
      } else {
        reset();
      }

      bypass();
      navigate(`/${pagesConfig.rulesEngine.link}`);
    },
    onError: (error) => {
      const errorMessage =
        pageMode === 'edit'
          ? Messages.Errors.Network.Updating('this automation rule')
          : Messages.Errors.Network.Adding('this automation rule');
      handleErrors({
        error,
        snackbarMessage: errorMessage,
      });
    },
    onSettled: (_returnData, _error, data) => {
      const eventType =
        pageMode === 'edit'
          ? analytics.automation.rules_engine.edit_rule
          : analytics.automation.rules_engine.create_rule;

      const eventPayload = {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.rulesEngine,
        rule: {
          triggerGroup: data.triggerInfo?.triggerGroup,
          triggerEvent: data.triggerInfo?.triggerEvent,
          occursEverytime: data.triggerInfo?.occursEverytime,
          triggerThreshold: data.triggerInfo?.triggerThreshold,
          hasTimeDuration: data.triggerInfo?.hasTimeDuration,
          triggerDuration: data.triggerInfo?.triggerDuration,
          triggerDurationType: data.triggerInfo?.triggerDurationType,
          triggerUserSources: data.triggerInfo?.triggerUserSources?.length || 0,
          triggerCalendarSources:
            data.triggerInfo?.triggerCalendarSources?.length || 0,
          triggerGroupSources:
            data.triggerInfo?.triggerGroupSources?.length || 0,
          triggerOrgSources: data.triggerInfo?.triggerOrgSources?.length || 0,
          triggerDomainSources:
            data.triggerInfo?.triggerDomainSources?.length || 0,
          sharedDriveIds: data.triggerInfo?.sharedDriveIds?.length || 0,
          driveRegExs: data.triggerInfo?.driveRegExs?.length || 0,
          action: data.actionInfo?.actionTodo,
        },
      };

      createEvent(eventType, eventPayload);
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<RulesEngineForm> = async (data) => {
    const requiredRole =
      pageMode === 'edit'
        ? RulesEngineRoles.EditRules.value
        : RulesEngineRoles.AddRules.value;

    if (isAbleToSeeUIElement(changeLogRoles, requiredRole)) {
      setDialogSubmitAction('publish');
      setOpenChangeLogDialog(true);
    } else {
      const isValid = await trigger();
      if (isValid) {
        mutate(data);
      }
    }
  };

  const handleCancel = () => {
    const destination = fromTemplates
      ? `/${pagesConfig.rulesEngine.link}/${pagesConfig.browseTemplates.link}`
      : `/${pagesConfig.rulesEngine.link}`;
    navigate(destination, {
      state: { fromCancelButton: true },
    });
  };

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    isCancelAction,
    bypass,
  } = useUnsavedChanges(isDirty);

  const handleSaveAndClose = () => {
    const requiredRole =
      pageMode === 'edit'
        ? RulesEngineRoles.EditRules.value
        : RulesEngineRoles.AddRules.value;
    if (isAbleToSeeUIElement(changeLogRoles, requiredRole)) {
      setDialogSubmitAction('draft');
      setOpenChangeLogDialog(true);
    } else {
      const currentData = getValues();
      saveDraft({ ...currentData, showSnackbar: true });
    }
  };

  const handleCreatePolicy = () => {
    const currentData = getValues();
    saveDraft(
      { ...currentData, showSnackbar: false },
      {
        onSuccess: (savedRule) => {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Rule saved as draft. You can resume editing anytime.',
          });
          bypass();
          navigate(
            `/${pagesConfig.policies.link}/${pagesConfig.createPolicy.link}`,
            { state: { resumeRuleId: savedRule.id } }
          );
        },
      }
    );
  };

  const handleCreateEmailTemplate = () => {
    const currentData = getValues();
    saveDraft(
      { ...currentData, showSnackbar: false },
      {
        onSuccess: (savedRule) => {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Rule saved as draft. You can resume editing anytime.',
          });
          bypass();
          navigate(
            `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}/create`,
            { state: { resumeRuleId: savedRule.id } }
          );
        },
      }
    );
  };

  const isLoadingSharedDrives =
    sharedDriveIdsToFetch.length > 0 && !sharedDrives;

  const isRuleDataLoading =
    isLoadingAllRules ||
    pendingUserDriveFolders ||
    isLoadingSharedDrives ||
    isLoadingCalendars;

  const isLoading =
    isPolicyLoading || (pageMode !== 'create' && isRuleDataLoading);

  function getFirstIdWithError(): string | null {
    if (errors.policyInfo) return 'runPolicy';
    if (errors.emailInfo) return 'sendEmail';
    if (errors.chatInfo) return 'sendChatMessage';
    if (errors.groupInfo) return 'addToGroup';
    if (errors.removeGroupInfo) return 'removeFromGroup';
    if (errors.chatInfo) return 'sendChatMessage';
    if (errors.actionInfo?.occurrenceLimit) return 'limitOccurrences';
    return null;
  }

  const firstIdWithError = getFirstIdWithError();

  const handleOpenDrawer = (rule: RuleModel) => {
    setSearchParams(
      (prev) => {
        prev.set('selectedRule', rule.id.toString());
        return prev;
      },
      { state: location.state }
    );
  };

  const handleCloseDrawer = () => {
    if (pageMode === 'edit') {
      navigate(
        `/${pagesConfig.rulesEngine.link}/edit/${selectedRuleIdFromPath}`
      );
    } else {
      setSearchParams(
        (prev) => {
          prev.delete('selectedRule');
          return prev;
        },
        { state: location.state }
      );
    }
  };

  const updateSelectedRuleStatus = (status: 'Enabled' | 'Disabled') => {
    if (selectedRule) {
      // Also update the query cache for the main grid
      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[] | undefined) => {
          return oldData
            ? oldData.map((r) =>
                r.id === selectedRule.id
                  ? { ...r, basicInfo: { ...r.basicInfo, status } }
                  : r
              )
            : undefined;
        }
      );
    }
  };
  const handleEnableRule = () => {
    setOpenEnableRuleDialog(true);
  };

  const handleDisableRule = () => {
    setOpenDisableRuleDialog(true);
  };

  const handleDeleteRule = () => {
    setOpenDeleteRuleDialog(true);
  };

  const handleEditRule = (rule: RuleModel) => {
    if (rule.basicInfo.status === 'Draft') {
      navigate(`/${pagesConfig.rulesEngine.link}/edit/${rule.id}`);
    } else {
      setOpenEditRuleDialog(true);
    }
  };

  const handleCloneRule = (ruleToClone: RuleModel) => {
    if (!ruleToClone) return;
    navigate(
      `/${pagesConfig.rulesEngine.link}/${pagesConfig.cloneRule.link}/${ruleToClone.id}`
    );
  };

  const handleDeleteSuccess = () => {
    if (!selectedRule) return;
    queryClient.setQueryData(
      [RulesKeys.RulesList],
      (oldData: RuleModel[] | undefined) =>
        oldData ? oldData.filter((r) => r.id !== selectedRule.id) : oldData
    );
    handleCloseDrawer();
    setOpenDeleteRuleDialog(false);
  };

  // Feature-flagged. More steps will be populated as the UI/UX improvement tickets are implemented.
  const baseSteps: StepConfig[] = useMemo(
    () => [
      {
        id: 'rules-engine-basic-info-step',
        label: 'Basic Info',
        header: 'Basic Information',
        schema: RulesEngineBasicInformationStepSchema,
        icon: TextSnippet,
        component: <RulesEngineBasicInformationStep control={control} />,
      },
      {
        id: 'rules-engine-when-step',
        label: 'When',
        header: 'WHEN this happens',
        subheader: 'Choose what triggers this rule.',
        schema: RulesEngineWhenStepSchema,
        icon: Bolt,
        component: (
          <RulesEngineWhenStep
            key={ruleId}
            control={control}
            errors={errors}
            setValue={setValue}
            trigger={trigger}
            sourceType={sourceType as RulesEngineSourceType}
            setSourceType={setUserSelectedSourceType}
            watch={watch}
            domains={domains || []}
            setSelectedRule={handleOpenDrawer}
            allRules={allRulesFromApi ?? []}
            clearErrors={clearErrors}
            isPersonalCalendar={isPersonalCalendar}
            resourceCalendars={resourceCalendars ?? []}
          />
        ),
      },
      {
        id: 'rules-engine-conditions-step',
        label: 'If',
        header: 'IF these conditions are met',
        subheader: 'Optional filters that refine when the rule fires.',
        schema: ConditionBlocksSchema,
        icon: FilterAltIcon,
        component: (
          <ConditionBlocks
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            domains={domains || []}
            isPersonalCalendar={isPersonalCalendar}
          />
        ),
      },
      {
        id: 'rules-engine-then-step',
        label: 'Then',
        header: 'THEN do this',
        subheader: 'The action taken when this rule fires',
        schema: ActionsSchema,
        icon: PlayArrow,
        component: (
          <ThenAction
            control={control}
            errors={errors}
            watch={watch}
            trigger={trigger}
            allPolicies={allPolicies || []}
            expandedIds={expandedIds}
            setExpandedIds={setExpandedIds}
            setValue={setValue}
            setActionSubSteps={setActionSubSteps}
            clearErrors={clearErrors}
            toolsRoles={toolsRoles}
            onCreatePolicy={handleCreatePolicy}
            onCreateEmailTemplate={handleCreateEmailTemplate}
            isSavingDraft={isSavingDraft}
          />
        ),
      },
    ],
    [
      allRulesFromApi,
      clearErrors,
      control,
      domains,
      errors,
      watch,
      handleOpenDrawer,
      setValue,
      sourceType,
      trigger,
      triggerGroup,
      allPolicies,
      expandedIds,
      isSavingDraft,
      setActionSubSteps,
      setExpandedIds,
      toolsRoles,
      handleCreatePolicy,
      isPersonalCalendar,
      isPersonalCalendar,
      handleCreateEmailTemplate,
    ]
  );
  const steps = useMemo(() => {
    const stepVisibilityRules: {
      [key: string]: (group: string, event: string) => boolean;
    } = {
      If: (group, event) => {
        const showDriveFilters =
          group === 'drive' &&
          ['create', 'download', 'change_user_access', 'change_owner'].includes(
            event
          );
        const showAdminFilters =
          group === 'admin' &&
          [
            'CREATE_USER',
            'ROLE_PERMISSION_CHANGE',
            'UPDATE',
            'CHANGE_PASSWORD',
            'ROLE_MEMBER_UPDATE',
          ].includes(event);
        const showGroupFilters =
          (group === 'admin' &&
            [
              'ADD_GROUP_MEMBER',
              'REMOVE_GROUP_MEMBER',
              'UPDATE_GROUP_MEMBER',
            ].includes(event)) ||
          group === 'groupEvents';
        const showPersonalCalendarFilters =
          group === 'calendar' && ['change_calendar_acls'].includes(event);
        return (
          showDriveFilters ||
          showAdminFilters ||
          showGroupFilters ||
          showPersonalCalendarFilters
        );
      },
    };

    return baseSteps.filter(
      (step) =>
        stepVisibilityRules[step.label]?.(triggerGroup, triggerEvent) ?? true
    );
  }, [baseSteps, triggerGroup, triggerEvent]);

  const stepIsLocked = steps.map(() => false);

  const formStepperProps = {
    activeStep,
    setActiveStep,
    stepIsLocked,
    steps,
    errors,
    trigger,
    control,
    getValues,
  };

  useEffect(() => {
    if (hasUserDriveFolderError) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          'Failed to load some folder details. Please refresh the page. Saving now may result in data loss.',
      });
    }
  }, [hasUserDriveFolderError, setSnackbar]);

  // Handle invalid clone or edit ID after loading is complete
  if (!isLoadingAllRules && pageMode !== 'create' && !sourceRuleForForm) {
    return <NotFoundPage />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <SharedPageHeader
        breadcrumbs={[
          { label: ModuleList.modules.automation.name },
          {
            label: pagesConfig.rulesEngine.name,
            path: pagesConfig.rulesEngine.link,
          },
        ]}
        isSubPage
      />
      <SharedToolbarFormPage
        isLoadingData={isLoading}
        loadingSkeleton={<RulesEngineFormSkeleton />}
        title={title}
        {...formStepperProps}
        subStep={actionSubSteps}
        handleDiscard={handleCancel}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        submitButtonText="Save & Enable"
        isLoadingSubmit={isValidating || isSubmitting || isPending}
        isDisableSubmit={isLoading}
        firstIdWithError={firstIdWithError ?? undefined}
        setExpandedIds={setExpandedIds}
        lastSavedDate={
          lastSavedDate ?? sourceRuleForForm?.basicInfo.lastModifiedDate
        }
        hasBeenSaved={hasBeenSaved}
        handleSaveAndClose={handleSaveAndClose}
        isSaving={isSavingDraft}
        isFormDirty={isDirty || fromTemplates}
        status={selectedRule?.basicInfo.status}
        topContent={(props) => <RulesEngineHelp {...props} />}
        rightPanel={
          <Stack
            sx={{
              height: '100%',
              width: '100%',
              display: { xs: 'none', lg: 'block' },
            }}
          >
            <RuleSummaryPanel
              watch={watch}
              getModel={() => prepareRuleData(getValues())}
              disablePreview={['Actions'].includes(steps[activeStep]?.label)}
              resourceCalendars={resourceCalendars}
            />
          </Stack>
        }
      />

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        pageMode={pageMode}
        entityName="Rule"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setDialogSubmitAction(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (dialogSubmitAction === 'publish') {
            mutate(getValues());
          } else if (dialogSubmitAction === 'draft') {
            saveDraft({ ...getValues(), showSnackbar: true });
          }
        }}
        mutateLoading={isPending || isSavingDraft}
      />

      {selectedRule && (
        <RulesEngineDrawerDetails
          selectedRule={selectedRule}
          allPolicies={allPolicies || []}
          rules={allRulesFromApi || []}
          onClose={handleCloseDrawer}
          onEnable={handleEnableRule}
          onDisable={handleDisableRule}
          onDelete={handleDeleteRule}
          onEdit={handleEditRule}
          onClone={handleCloneRule}
          openRulesDetailsDrawer={openRulesDetailsDrawer}
        />
      )}

      {openDeleteRuleDialog && selectedRule && (
        <DeleteRuleDialog
          open={openDeleteRuleDialog}
          handleClose={() => setOpenDeleteRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={handleDeleteSuccess}
        />
      )}
      {openDisableRuleDialog && selectedRule && (
        <DisableRuleDialog
          open={openDisableRuleDialog}
          handleClose={() => setOpenDisableRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={() => updateSelectedRuleStatus('Disabled')}
        />
      )}
      {openEnableRuleDialog && selectedRule && (
        <EnableRuleDialog
          open={openEnableRuleDialog}
          handleClose={() => setOpenEnableRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={() => updateSelectedRuleStatus('Enabled')}
        />
      )}
      {openEditRuleDialog && selectedRule && (
        <EditRuleDialog
          open={openEditRuleDialog}
          handleClose={() => setOpenEditRuleDialog(false)}
          rule={selectedRule}
          onEditExisting={() => {
            setOpenEditRuleDialog(false);
            navigate(
              `/${pagesConfig.rulesEngine.link}/edit/${selectedRule.id}`
            );
          }}
          onCreateNew={() => {
            const view = searchParams.get('view') || '';
            navigate(
              `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}${
                view ? `?view=${view}` : ''
              }`
            );
            handleCloseDrawer();
            setOpenEditRuleDialog(false);
            reset();
          }}
        />
      )}
    </Stack>
  );
}
